import { getUserInformation } from "@/apis/register/get";
import type { Property } from "@/apis/register/post/type";
import type { Experience } from "@/apis/register/type";
import type { FetchAPIResponse } from "@/apis/type";
import type {
  TableFiveData,
  TableSixData,
  TableTwoData,
} from "@/components/tables/type";
import router from "@/router";
import dayjs, { type Dayjs } from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import duration from "dayjs/plugin/duration";
import _ from "lodash";
import words from "naughty-words/th.json";
import type {
  AutoGetUserInfoDataRequest,
  AutoGetUserInfoDataResponse,
  UseRegisterParams,
} from "./type";

dayjs.extend(advancedFormat);
dayjs.extend(duration);

const isDevelop = import.meta.env.VITE_DEVELOP_MODE === "true" ? true : false;

function debugConsole(value: any, source = "debugConsole") {
  if (isDevelop) console.log(`${source}:`, value);
}

function useRegisterParams(): UseRegisterParams {
  const {
    value: { params },
  } = router.currentRoute;

  const safeNumberConvert = (value: unknown): number | undefined => {
    if (typeof value !== "string") return undefined;
    const num = Number(value);
    return Number.isSafeInteger(num) ? num : undefined;
  };

  return {
    formType: params?.formType?.toString().toLowerCase(),
    senateRecruitDetailId: safeNumberConvert(params?.senateRecruitDetailId),
    candidateInfoId: safeNumberConvert(params?.candidateInfoId),
  };
}

function debugConsoleV2(
  message: unknown,
  type: "log" | "error" | "warn" = "log",
): void {
  if (isDevelop && ["log", "error", "warn"].includes(type)) {
    console[type](message);
  } else if (!console[type]) {
    console.error("Invalid console method:", type);
  }
}

function days(diffMs: number, add: number = 0) {
  const oneDayMs = 1000 * 60 * 60 * 24;
  return Math.floor(diffMs / oneDayMs) + add;
}

function ymd(diffDays: number) {
  const y = Math.floor(diffDays / 365);
  const m = diffDays === 365 ? 0 : Math.floor(diffDays / 30.44) % 12;
  const calDays = diffDays - y * 365 - Math.floor(m * 30.44);
  const d = calDays < 0 ? 0 : calDays;
  return { y, m, d };
}

function differentDate(
  startDate?: Dayjs | string,
  endDate?: Dayjs | string,
  add: number = 0,
): { y: number; m: number; d: number; days: number } {
  if (!startDate || !endDate) return { y: 0, m: 0, d: 0, days: 0 };

  const s: Dayjs = typeof startDate === "string" ? dayjs(startDate) : startDate;
  const e: Dayjs = typeof endDate === "string" ? dayjs(endDate) : endDate;

  if (!s.isValid() || !e.isValid()) return { y: 0, m: 0, d: 0, days: 0 };

  const diffDays: number = e.diff(s, "day") + add;

  const totalDays = dayjs.duration(diffDays, "days");
  const y = totalDays.years();
  const m = totalDays.months();
  const d = totalDays.days();

  return { y, m, d, days: diffDays || 0 };
}

function getDateDiffInYMD(
  startDate?: Dayjs | string,
  endDate?: Dayjs | string,
  add: number = 0,
): string {
  if (!startDate || !endDate) return "-";
  const { y, m, d } = differentDate(startDate, endDate, add);
  return thaiAge(y, m, d);
}

function thaiAge(y: number, m: number, d: number): string {
  if (y === 0 && m === 0 && d === 0) return "-";
  const years: string = y > 0 ? `${y} ปี ` : "";
  const months: string = m > 0 ? `${m} เดือน ` : "";
  const days: string = d > 0 ? `${d} วัน` : "";
  return `${years}${months}${days}`.trim();
}

function formatStringFromDays(numberOfDays: number) {
  if (numberOfDays <= 0) return "";
  const { y, m, d } = ymd(numberOfDays);
  return thaiAge(y, m, d);
}

function disabledStartDate(current: Dayjs, endDate?: Dayjs) {
  if (endDate) {
    const result =
      current &&
      (current > dayjs().endOf("day") || current > dayjs(endDate).endOf("day"));
    return result;
  }
  return current && current > dayjs().endOf("day");
}

function disabledEndDate(current: Dayjs, startDate?: Dayjs) {
  return (
    current &&
    (current < dayjs(startDate).startOf("day") ||
      current > dayjs().endOf("day"))
  );
}

function getBase64FromFile(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

function fileSize(file: File, maximumSize = 2) {
  const size = file.size / 1024 / 1024;
  if (size > maximumSize) return false;
  return true;
}

function getAllId(arr1: any[], arr2: number[]) {
  const data: any = arr1.filter(({ id }) => id).map(({ id }) => id);
  if (data.length) arr2.push(...data);
}

function mergeDataTableTwo(
  resultArray: Property[],
  experienceType: number,
  dataTable: TableTwoData[],
) {
  if (dataTable.length) {
    const data = dataTable.map(
      ({ id, startDate, endDate, jobPosition }: TableTwoData) => {
        const diff = diffBetweenDates(startDate!, endDate!, -1);
        return {
          id,
          experienceType,
          title: jobPosition,
          startDate: startDate?.toISOString() || null,
          endDate: endDate?.toISOString() || null,
          eYear: diff.years,
          eMonth: diff.months,
          eDay: diff.days,
        };
      },
    );
    resultArray.push(...data);
  }
}

function propertyValidator(activeTable: number[], dataTable: TableTwoData[][]) {
  return dataTable.every((item, index) => {
    const con1 = activeTable.some((item) => item === index + 1);
    if (!con1) return true;

    const con2 = item.length ? true : false;
    if (con1 && con2) return true;

    return false;
  });
}

function ageRange() {
  const { formType } = useRegisterParams();
  let minimum = 45;
  let maximum = 70;
  let minimumTH = "สี่สิบห้า";
  let maximumTH = "เจ็ดสิบ";

  switch (formType) {
    case "pmec":
    case "nbtc":
      minimum = 40;
      minimumTH = "สี่สิบ";
      break;
    case "cct":
      maximum = 68;
      maximumTH = "หกสิบแปด";
      break;
    default:
      break;
  }

  return { minimum, maximum, minimumTH, maximumTH };
}

function ageCriteria(birthday?: Dayjs) {
  const today = dayjs();
  const { minimum, maximum } = ageRange();
  const age = today.diff(birthday, "year", true);

  if (age < minimum || age >= maximum) {
    return { pass: false, message: "ท่านอาจเป็นผู้ขาดคุณสมบัติ" };
  }
  return { pass: true, message: "" };
}

function ageCriteria2(minimum: number, maximum: number, birthday?: Dayjs) {
  const today = dayjs();
  const age = today.diff(birthday, "year", true);

  if (age < minimum || age >= maximum) {
    return { pass: false, message: "ท่านอาจเป็นผู้ขาดคุณสมบัติ" };
  }
  return { pass: true, message: "" };
}

async function validateYear(_rule: any, value: any) {
  const year = parseInt(dayjs().format("BBBB"));
  if (parseInt(value) > year) {
    return Promise.reject("");
  } else {
    return Promise.resolve();
  }
}

function isActiveProperty(array: number[], target: number) {
  return array.some((item) => item === target);
}

async function validatePhone(_rule: any, value: any) {
  if (value.length >= 0 && value.length < 9) {
    return Promise.reject("");
  } else {
    return Promise.resolve();
  }
}

function getNumberData(condition: any, insteadOf: any = undefined) {
  const value = condition !== null ? condition : insteadOf;
  return value as any;
}

function mapExperience(data: any[]): Experience[] {
  return data?.length
    ? data.map((item: any) => {
        const isPresent = item?.isPresent === 1 ? true : false;
        const endDate = isPresent
          ? dayjs()
          : item?.endDate
            ? dayjs(item.endDate)
            : undefined;
        return {
          id: item.id,
          position: item?.position || "",
          positionLevel: item?.positionLevel || "",
          positionDescription: item?.positionDescription || "",
          startDate: item?.startDate ? dayjs(item.startDate) : undefined,
          endDate,
          isPresent,
          org: item?.org || "",
          orgDescription: item?.orgDescription || "",
          place: item?.place || "",
          phone: item?.phone || "",
          showTextDay: item?.showTextDay || ""
        };
      })
    : [];
}

function calculateLatestStep(step: number | undefined, lastStep: number) {
  if (!step) return lastStep;
  return step > lastStep ? step : lastStep;
}

function setWithExpiry(key: string, value: any, ttl: number) {
  const now = new Date();
  const item = {
    value: value,
    expiry: now.getTime() + ttl,
  };
  localStorage.setItem(key, JSON.stringify(item));
}

function getWithExpiry<T = any>(key: string) {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) return null;

  const item = JSON.parse(itemStr);

  const now = new Date();
  if (now.getTime() > item.expiry) {
    localStorage.removeItem(key);
    return null;
  }

  return item.value as FetchAPIResponse<T>;
}

function windowScroll(top: number = 0, left: number = 0) {
  window.scroll({
    top,
    left,
    behavior: "smooth",
  });
}

function assertThaiId(thaiId?: string): boolean {
  if (!thaiId) return false;
  const m = thaiId.match(/(\d{12})(\d)/);
  if (!m) return false;
  const digits = m[1].split("");
  const sum = digits.reduce((total: number, digit: string, i: number) => {
    return total + (13 - i) * +digit;
  }, 0);
  const lastDigit = `${(11 - (sum % 11)) % 10}`;
  const inputLastDigit = m[2];
  if (lastDigit !== inputLastDigit) return false;
  return true;
}

function pagination(length: number, currentPage: number, itemsPerPage: number) {
  return {
    total: length,
    per_page: itemsPerPage,
    current_page: currentPage,
    last_page: Math.ceil(length / itemsPerPage),
    from: (currentPage - 1) * itemsPerPage,
    to: currentPage * itemsPerPage,
  };
}

function isArrayEqual(x: any, y: any) {
  return _(x).differenceWith(y, _.isEqual).isEmpty();
}

async function validateIdentityCard(_rule: any, value: any) {
  if (!assertThaiId(value)) {
    return Promise.reject("");
  } else {
    return Promise.resolve();
  }
}

function checkRude(_rule: any, value: any) {
  const c = words.some((word) => new RegExp(word, "g").test(value));
  if (c) {
    return Promise.reject("");
  } else {
    return Promise.resolve();
  }
}

async function clientIP() {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    const result = await response.json();
    return result;
  } catch (error) {
    debugConsole(error, "clientIP");
  }
}

function getQueryNumber(value: string | undefined) {
  return value !== undefined && !isNaN(Number(value))
    ? Number(value)
    : undefined;
}

function formatFileName(str: string) {
  return str.slice(str.indexOf("__") + 2);
}

function convertYearsToThai() {
  const cells = document.querySelectorAll(".ant-picker-cell-inner");
  console.log("cells: ", cells);
  cells.forEach((cell) => {
    if (cell.textContent !== null) {
      const year = parseInt(cell.textContent);
      if (!isNaN(year)) {
        const thaiYear = year + 543;
        cell.textContent = thaiYear.toString();
        cell.setAttribute("title", thaiYear.toString());
      }
    }
  });
}

const downloadFile = (data: any, filename: string) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
};

const autoGetUserInfoData = async (
  request: AutoGetUserInfoDataRequest,
): Promise<AutoGetUserInfoDataResponse | null> => {
  try {
    const { status, data } = await getUserInformation(request);

    if (status === 200 && data) {
      const { prefix = "", firstName = "", lastName = "" } = data;
      return { prefix, firstName, lastName };
    } else {
      debugConsoleV2(`Failed to fetch user data, status: ${status}`, "error");
      return null;
    }
  } catch (error) {
    debugConsoleV2(`Error fetching user information: ${error}`, "error");
    return null;
  }
};

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function diffBetweenDates(
  startDate: Dayjs,
  endDate: Dayjs,
  addDays: number = 0,
) {
  const adjustedEndDate = endDate.add(addDays, "day");

  const sYear = parseInt(startDate.format("YYYY"));
  const sMonth = parseInt(startDate.format("MM"));
  const sDay = parseInt(startDate.format("DD"));

  const eYear = parseInt(adjustedEndDate.format("YYYY"));
  const eMonth = parseInt(adjustedEndDate.format("MM"));
  const eDay = parseInt(adjustedEndDate.format("DD"));

  let years = eYear - sYear;
  let months = eMonth - sMonth;
  let days = eDay - sDay;

  if (days < 0) {
    months--;
    const daysInPrevMonth = adjustedEndDate.subtract(1, "month").daysInMonth();
    days += daysInPrevMonth;
  }

  if (months < 0) {
    years--;
    months += 12;
  }

  return { years, months, days };
}


function getAge(
  startDate?: Dayjs,
  endDate?: Dayjs,
  addDays: number = 0,
): string {
  if (!startDate || !endDate) return "-";

  const { years, months, days } = diffBetweenDates(startDate, endDate, addDays);

  const yearString = "ปี";
  const monthString = "เดือน";
  const dayString = "วัน";

  const parts = [];

  if (years > 0) {
    parts.push(`${years} ${yearString}`);
  }
  if (months > 0) {
    parts.push(`${months} ${monthString}`);
  }
  if (days > 0) {
    parts.push(`${days} ${dayString}`);
  }

  return parts.length > 0 ? parts.join(" ") : "-";
}

function getAge2(
  data?: string,
): string {
  if (!data) return "-";
  return data;
}

function intDivide(dividend: number, divisor: number) {
  let quotient = dividend / divisor;
  return quotient < 0 ? Math.ceil(quotient) : Math.floor(quotient);
}

function mapTableFiveData(item: any): TableFiveData {
  return {
    id: item?.id || null,
    name: item?.name || "",
    startDate: item?.startDate ? dayjs(item.startDate) : undefined,
    endDate: item?.endDate ? dayjs(item.endDate) : undefined,
  };
}

function mapTableSixData(item: any): TableSixData {
  return {
    id: item?.id || null,
    position: item?.position || "",
    name: item?.name || "",
    startDate: item?.startDate ? dayjs(item.startDate) : undefined,
    endDate: item?.endDate ? dayjs(item.endDate) : undefined,
  };
}

function groupByType(list: any[]) {
  return list.reduce(
    (acc, item) => {
      if (item.type === "M") acc.m1.push(item);
      if (item.type === "P") acc.p1.push(item);
      if (item.type === "M2") acc.m2.push(item);
      if (item.type === "P2") acc.p2.push(item);
      return acc;
    },
    { m1: [], p1: [], m2: [], p2: [] } as Record<string, any[]>,
  );
}

export {
  ageCriteria,
  ageCriteria2,
  ageRange,
  assertThaiId,
  autoGetUserInfoData,
  calculateLatestStep,
  checkRude,
  clientIP,
  convertYearsToThai,
  debugConsole,
  debugConsoleV2,
  diffBetweenDates,
  differentDate,
  disabledEndDate,
  disabledStartDate,
  downloadFile,
  fileSize,
  formatFileName,
  formatStringFromDays,
  getAge,
  getAllId,
  getBase64FromFile,
  getDateDiffInYMD,
  getNumberData,
  getQueryNumber,
  getWithExpiry,
  groupByType,
  intDivide,
  isActiveProperty,
  isArrayEqual,
  isDevelop,
  isLeapYear,
  mapExperience,
  mapTableFiveData,
  mapTableSixData,
  mergeDataTableTwo,
  pagination,
  propertyValidator,
  setWithExpiry,
  thaiAge,
  useRegisterParams,
  validateIdentityCard,
  validatePhone,
  validateYear,
  windowScroll,
  getAge2,
};
